import {
    Box,
    Button,
    Flex,
    Text,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import React from 'react'
import ProductTileImage from '../../product-tile/partials/product-tile-image'

import useProductData from '../../../hooks/use-product-data'
import {PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID} from '@salesforce/retail-react-app/app/constants'
import {FormattedMessage} from 'react-intl'

import DisplayPrice from '../../display-price'
import OmnibusPrice from '../../display-price/omnibus-price'
import {getPriceData} from '../../../utils/product-utils'

import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import Link from '@salesforce/retail-react-app/app/components/link'

import {useGoogleTagManager} from '../../../custom-api/hooks/use-google-tag-manager'
import {item_gtm_event} from '../../../custom-api/layers/default'

import {useBooleanCustomPreference} from '../../../hooks/use-custom-preference'

const ProductTileRecommended = ({
    variant,
    tileImageVariant,
    product,
    badgeDetails,
    productIndex = undefined,
    style,
    prefix = 'default',
    isDragging = false,
}) => {
    const styles = useMultiStyleConfig('RecommendedProducts', {variant})
    const {currency} = useCurrency()
    const {addLayer} = useGoogleTagManager()
    const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')

    const {productUrl, priceData} = useProductData(product, PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID)

    const selectItemTileEventGtm = async () => {
        await addLayer(
            item_gtm_event.bind(this, {
                event: 'select_item',
                item_list_name: 'carousel_pdp',
                items: [
                    {
                        item_name: product?.c_defaultName,
                        item_id: product?.id,
                        item_category: product?.c_defaultCategoryName,
                        item_variant: product?.variants?.[0]?.productId,
                        price: +(product?.price / (1 + product?.c_taxRate / 100)).toFixed(2),
                        fullprice: getPriceData(product)?.isOnSale ? 'reduced' : 'fullprice',
                        rating_points: '',
                        // get_reevoo_rating() || get_bv_rating(),
                        erp_category: product?.c_materialGroup,
                        prdha_category: product?.c_prdha_category,
                        index: productIndex + 1,
                        stock_status:
                            product?.inventory?.stockLevel > 0 ? 'in-stock' : 'out-of-stock',
                    },
                ],
            }),
        )
    }

    return (
        <Flex
            {...styles.recommendedProductCard}
            className={`recommended-products-pruduct-tile-${prefix}`}
            style={style}
        >
            <ProductTileImage
                badgeDetails={badgeDetails}
                product={product}
                enableFavourite
                productUrl={productUrl}
                variant={tileImageVariant}
                priceData={priceData}
                selectItemTileEventGtm={selectItemTileEventGtm}
                isDragging={isDragging}
            />
            <Flex {...styles.productDetailsContainer}>
                <Text {...styles.productDetailId}>
                    {product?.c_subModelIdentifier || product?.id}
                </Text>
                <Box>
                    <Text {...styles.productDetailName}>{product.name}</Text>
                </Box>
                <Flex flexDirection={'column'}>
                    <DisplayPrice
                        priceData={priceData}
                        currency={currency}
                        listPriceProps={{...styles.productListPrice}}
                        currentPriceProps={{...styles.productDetailPrice}}
                        discountProps={{display: 'none'}}
                        productDetailPriceView={true}
                        hideListingPrice={true}
                        notShoppableCountry={notShoppableCountry}
                    />
                    <Box {...styles.productOmnibusWrapper}>
                        {!notShoppableCountry && priceData.showOmnibusPriceDiscount && (
                            <OmnibusPrice
                                priceData={priceData}
                                currency={currency}
                                omnibusExpandedView={true}
                                variant="recommended"
                            />
                        )}
                    </Box>
                </Flex>
            </Flex>
            <Flex>
                <Button
                    variant="primary"
                    as={Link}
                    to={productUrl}
                    w="100%"
                    px={0}
                    onClick={selectItemTileEventGtm}
                    {...styles.discoverMoreButton}
                    data-testid="button"
                >
                    <FormattedMessage
                        defaultMessage="Discover More"
                        id="recommended_product_card.discover_more_button"
                    />
                </Button>
            </Flex>
        </Flex>
    )
}

ProductTileRecommended.propTypes = {
    variant: PropTypes.string,
    tileImageVariant: PropTypes.string,
    product: PropTypes.object,
    badgeDetails: PropTypes.array,
    prefix: PropTypes.string,
    productIndex: PropTypes.number,
    style: PropTypes.object,
    isDragging: PropTypes.bool,
}

export default ProductTileRecommended
